import {
  displayValueListResponseSchema,
  displayValueSchema,
  masterDataListResponseSchema,
  masterDataRecordSchema,
  operationsBackupsResponseSchema,
  roleAssignmentListResponseSchema,
  roleAssignmentViewSchema,
  updateValidationRulesRequestSchema,
  validationRulesResponseSchema,
  type DisplayValueKind,
  type DisplayValueListResponse,
  type DisplayValueUpdate,
  type MasterDataListResponse,
  type MasterDataRecordDto,
  type MasterDataSet,
  type MasterDataWrite,
  type MilestoneTypeWrite,
  type OperationsBackupsResponse,
  type PlatformWrite,
  type PosSegmentWrite,
  type PriorityWrite,
  type RoleAssignmentListResponse,
  type RoleAssignmentView,
  type UpdateRoleAssignmentRequest,
  type UpdateValidationRulesRequest,
  type ValidationRulesResponse,
} from '@complan/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';

/**
 * The Master_Data_Administration data layer (task 23.6), wired to the admin API
 * of tasks 21.1-21.4 and 21.2's status/finality surface.
 *
 * Follows the established hook style (`saved-view-data.ts`, `export-centre-data.ts`):
 * a thin `apiFetch` per endpoint, the wire body parsed through the `@complan/contracts`
 * schema so a drifted shape fails at the boundary, one React Query hook per read,
 * and mutations that invalidate only the affected key on success.
 *
 * What this module deliberately does **not** do is re-implement any server rule.
 * The referenced-delete refusal (R16.6), the Platform colour rules (R16.12), the
 * name-uniqueness check (R16.11), the fixed-value-set rejection (R16.10) and the
 * non-configurable-rule guard (R8.9) are all decided by the API and returned as
 * `validation_failed` findings; the screens surface those findings (via
 * `resolveFindingMessage`) rather than deciding anything locally. The one place a
 * write per-set shape is chosen is {@link MasterDataWritePayload}, the union the
 * record editor submits.
 */

/**
 * The write payload for a master data record. The nine sets share the common
 * shape; three sets add fields — `platform` a colour (R16.1), `priority` the
 * Elevated_Priority_Flag (R16.3), `milestone-type` the mandatory rule, the
 * Calendar_Relevance_Flag and the preceding type (R16.3), and `pos-segment`
 * the POS count and source timestamp (complan-manpower/requirements.md R4.2).
 * The editor builds the right shape for the set it edits and the API validates
 * it per-set.
 */
export type MasterDataWritePayload =
  | MasterDataWrite
  | PlatformWrite
  | PriorityWrite
  | MilestoneTypeWrite
  | PosSegmentWrite;

/** Fetches the records of a master data set, active-only or including inactive (R16.5). */
export async function fetchMasterDataSet(
  set: MasterDataSet,
  includeInactive: boolean,
): Promise<MasterDataListResponse> {
  const query = includeInactive ? '?includeInactive=true' : '';
  const body = await apiFetch<unknown>(`/api/admin/master-data/${set}${query}`);
  return masterDataListResponseSchema.parse(body);
}

/** React Query hook for one master data set's records (R16.1-R16.3, R16.5). */
export function useMasterDataSet(
  set: MasterDataSet,
  includeInactive: boolean,
): UseQueryResult<MasterDataListResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.masterData(set, includeInactive),
    queryFn: () => fetchMasterDataSet(set, includeInactive),
  });
}

/** Invalidates both the active-only and all-records views of a set after a write. */
function invalidateMasterDataSet(
  queryClient: ReturnType<typeof useQueryClient>,
  set: MasterDataSet,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.masterData(set, false) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.masterData(set, true) });
}

/**
 * Creates a master data record (R16.1-R16.3). A rule rejection (duplicate active
 * name R16.11, a Platform colour violation R16.12) comes back as a
 * `validation_failed` the caller surfaces from the thrown {@link ApiRequestError}.
 */
export function useCreateMasterDataRecord(
  set: MasterDataSet,
): UseMutationResult<MasterDataRecordDto, unknown, MasterDataWritePayload> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: MasterDataWritePayload) => {
      const body = await apiFetch<unknown>(`/api/admin/master-data/${set}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return masterDataRecordSchema.parse(body);
    },
    onSuccess: () => invalidateMasterDataSet(queryClient, set),
  });
}

/**
 * Edits a master data record, including activation and deactivation via the
 * `isActive` field (R16.1-R16.3). Deactivation is an ordinary edit — the record
 * keeps its associations and is only withheld from new selections (R16.5).
 */
export function useEditMasterDataRecord(
  set: MasterDataSet,
): UseMutationResult<
  MasterDataRecordDto,
  unknown,
  { readonly id: string; readonly payload: MasterDataWritePayload }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const body = await apiFetch<unknown>(`/api/admin/master-data/${set}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return masterDataRecordSchema.parse(body);
    },
    onSuccess: () => invalidateMasterDataSet(queryClient, set),
  });
}

/**
 * Requests deletion of a master data record (R16.6). The API never hard-deletes:
 * the request is always refused with a `validation_failed` whose finding carries
 * the exact `referencingInitiativeCount` and offers `alternative: 'deactivate'`.
 * This mutation therefore always throws an {@link ApiRequestError} on a 422, and
 * the caller reads the finding to show the count and the deactivation offer. A
 * `not_found` (404) is the unknown-id case.
 */
export function useDeleteMasterDataRecord(
  set: MasterDataSet,
): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiFetch<unknown>(`/api/admin/master-data/${set}/${id}`, { method: 'DELETE' });
    },
    // A refused delete persists nothing, but a caller may retry as a deactivate,
    // so invalidate on settle to keep the table in step with any follow-up edit.
    onSuccess: () => invalidateMasterDataSet(queryClient, set),
  });
}

/** Fetches the values of the status set with their editable display attributes (R16.10). */
export async function fetchStatusFinality(
  kind: DisplayValueKind,
): Promise<DisplayValueListResponse> {
  const body = await apiFetch<unknown>(`/api/admin/status-finality/${kind}`);
  return displayValueListResponseSchema.parse(body);
}

/** React Query hook for a fixed value set's display values (R16.10). */
export function useStatusFinality(
  kind: DisplayValueKind,
): UseQueryResult<DisplayValueListResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.statusFinality(kind),
    queryFn: () => fetchStatusFinality(kind),
  });
}

/**
 * Updates the display label and display order of one member of a fixed value set
 * (R16.10). The value itself is fixed and is not part of the write — the endpoint
 * accepts only the label and the order, and rejects any create or delete.
 */
export function useUpdateStatusFinality(
  kind: DisplayValueKind,
): UseMutationResult<
  ReturnType<typeof displayValueSchema.parse>,
  unknown,
  { readonly value: string; readonly update: DisplayValueUpdate }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ value, update }) => {
      const body = await apiFetch<unknown>(
        `/api/admin/status-finality/${kind}/${encodeURIComponent(value)}`,
        { method: 'PUT', body: JSON.stringify(update) },
      );
      return displayValueSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.statusFinality(kind) });
    },
  });
}

/** Fetches every registered validation rule with its effective tier, message and active state (R8.9). */
export async function fetchValidationRules(): Promise<ValidationRulesResponse> {
  const body = await apiFetch<unknown>('/api/admin/validation-rules');
  return validationRulesResponseSchema.parse(body);
}

/** React Query hook for the validation-rule configuration (R8.9). */
export function useValidationRules(): UseQueryResult<ValidationRulesResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.validationRules(),
    queryFn: fetchValidationRules,
  });
}

/**
 * Applies a batch of validation-rule configuration changes (R8.9). A change to a
 * non-configurable rule is refused by the API as a `validation_failed`; the table
 * only submits configurable rules, so the refusal is the backstop, not the path.
 */
export function useUpdateValidationRules(): UseMutationResult<
  ValidationRulesResponse,
  unknown,
  UpdateValidationRulesRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateValidationRulesRequest) => {
      const body = await apiFetch<unknown>('/api/admin/validation-rules', {
        method: 'PUT',
        body: JSON.stringify(updateValidationRulesRequestSchema.parse(request)),
      });
      return validationRulesResponseSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.validationRules() });
    },
  });
}

/** Fetches every user's role assignment aggregate (R1.7, R2.4, R9.3). */
export async function fetchRoleAssignments(): Promise<RoleAssignmentListResponse> {
  const body = await apiFetch<unknown>('/api/admin/roles');
  return roleAssignmentListResponseSchema.parse(body);
}

/** React Query hook for the role-assignment list (R1.7, R2.4, R9.3). */
export function useRoleAssignments(): UseQueryResult<RoleAssignmentListResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.roleAssignments(),
    queryFn: fetchRoleAssignments,
  });
}

/**
 * Replaces one user's whole role assignment (R1.9). The change takes effect on
 * that user's next request without a sign-out (R1.10) because the server resolves
 * the Principal from this table every request.
 */
export function useUpdateRoleAssignment(): UseMutationResult<
  RoleAssignmentView,
  unknown,
  UpdateRoleAssignmentRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateRoleAssignmentRequest) => {
      const body = await apiFetch<unknown>('/api/admin/roles', {
        method: 'PUT',
        body: JSON.stringify(request),
      });
      return roleAssignmentViewSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.roleAssignments() });
    },
  });
}

/** Fetches the recorded backup runs and restore verifications (R17.6, R17.10). */
export async function fetchOperationsBackups(): Promise<OperationsBackupsResponse> {
  const body = await apiFetch<unknown>('/api/admin/operations/backups');
  return operationsBackupsResponseSchema.parse(body);
}

/** React Query hook for the operations backup/verification log (R17.6, R17.10). */
export function useOperationsBackups(): UseQueryResult<OperationsBackupsResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.operationsBackups(),
    queryFn: fetchOperationsBackups,
  });
}
